import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';

import * as ActionCreators from '../../actions/ActionCreators';
import * as ActionTypes from '../../actions/ActionTypes';
import * as Controls from '../UIControlsNames';
import { timecodeFromSeconds } from '../../utils/ASVPUtils';
import { getEmbedCode, getDirectLink, getTimecode } from './utils';
import { ASControl } from '../VideoJSComponents';
import ShareOverlay from './ShareOverlay';

import {
    FACEBOOK_SHARE_URL,
    TWITTER_SHARE_URL,
    TUMBLR_SHARE_URL
} from '../../constants';


const showShareOverlayEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.ON_SHARE_CONTROL_CLICK)
    .filter(() => !store.getState().player.adPlaying) // only when an ad isn't playing
    .filter(() => store.getState().player.share)
    .map(() => player.getChild(Controls.SHARE_OVERLAY)) // get share overlay
    .filter(shareOverlay => !shareOverlay) // only proceed if no share overlay already
    .map(() => store.getState().videoSource)
    .switchMap(videoSource => {
        player.pause();
        player.addClass('as-sharing');

        const embedCode = getEmbedCode(videoSource, options);
        const directLink = getDirectLink(videoSource, options);
        const timecode = getTimecode(videoSource, player.currentTime());

        ASControl(Controls.SHARE_OVERLAY, 'as-share-overlay', ShareOverlay({ directLink, timecode, embedCode }));
        const shareOverlay = player.addChild(Controls.SHARE_OVERLAY);
        const closeButton = shareOverlay.addChild(Controls.CLOSE_BUTTON);
        const timecodeCheckbox = shareOverlay.contentEl().querySelector('#timecode-selection');
        const timecodeInput = shareOverlay.contentEl().querySelector('#timecode-input');
        const facebookShareAnchor = shareOverlay.el().querySelector('.facebook-icon > a');
        const twitterShareAnchor = shareOverlay.el().querySelector('.twitter-icon > a');
        const tumblrShareAnchor = shareOverlay.el().querySelector('.tumblr-icon > a');
        const copyButtons = shareOverlay.el().querySelectorAll('.copy-button');
        const copyButtonObservers = Array.from(copyButtons).map((btn) => Observable.fromEvent(btn, 'click').map((e) => ActionCreators.onCopyClick(e)));

        return Observable.merge(
            Observable.fromEvent(closeButton, 'click').mapTo(ActionCreators.onShareControlClose()),
            Observable.fromEvent(facebookShareAnchor, 'click').map((e) => ActionCreators.onShareSocial(e)),
            Observable.fromEvent(twitterShareAnchor, 'click').map((e) => ActionCreators.onShareSocial(e)),
            Observable.fromEvent(tumblrShareAnchor, 'click').map((e) => ActionCreators.onShareSocial(e)),
            (timecodeCheckbox) ? Observable.fromEvent(timecodeCheckbox, 'change').map(e => ActionCreators.onTimecodeSelectionChange(e)) : Observable.empty(),
            (timecodeInput) ? Observable.fromEvent(timecodeInput, 'keyup').map((e) => ActionCreators.onTimecodeInputChange(e)) : Observable.empty(),
            ...copyButtonObservers
        );
    });


const onTimecodeInputChangeEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.ON_TIMECODE_INPUT_CHANGE)
    .pluck('payload', 'target', 'value')
    .do(timecode => {
        // update timecode display
        const shareOverlay = player.getChild(Controls.SHARE_OVERLAY);
        const timecodeText = shareOverlay.contentEl().querySelector('.timecode');
        timecodeText.innerHTML = timecode;

        // don't allow non-numeric, non-colon chars
        const timecodeInput = shareOverlay.contentEl().querySelector('#timecode-input');
        timecodeInput.value = timecode.replace(/[^0-9\:]+/, '');

        const directLink = getDirectLink(store.getState().videoSource, options, timecode);
        const embedCode = getEmbedCode(store.getState().videoSource, options, timecode);

        // update embed code value
        const embedCodeInput = shareOverlay.contentEl().querySelector('#embed-code-input');
        if (embedCodeInput) embedCodeInput.value = embedCode;

        // update direct link with timecode
        const directLinkInput = shareOverlay.contentEl().querySelector('#direct-link-input');
        if (directLinkInput) directLinkInput.value = directLink;

        //update social links with timecode
        const facebookShareAnchor = shareOverlay.el().querySelector('.facebook-icon > a');
        if (facebookShareAnchor) facebookShareAnchor.setAttribute('href', `${FACEBOOK_SHARE_URL}${directLink}`);
        const twitterShareAnchor = shareOverlay.el().querySelector('.twitter-icon > a');
        if (twitterShareAnchor) twitterShareAnchor.setAttribute('href', `${TWITTER_SHARE_URL}${directLink}`);
        const tumblrShareAnchor = shareOverlay.el().querySelector('.tumblr-icon > a');
        if (tumblrShareAnchor) tumblrShareAnchor.setAttribute('href', `${TUMBLR_SHARE_URL}${directLink}`);
    })
    .ignoreElements();


const useTimecodeShareEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.ON_TIMECODE_SELECTION_CHANGE)
    .pluck('payload', 'target', 'checked')
    .do((isChecked) => {
        const shareOverlay = player.getChild(Controls.SHARE_OVERLAY);
        const timecode = (isChecked) ? timecodeFromSeconds(player.currentTime()) : undefined;
        const directLink = getDirectLink(store.getState().videoSource, options, timecode);
        const embedCode = getEmbedCode(store.getState().videoSource, options, timecode);

        // update embed code value
        const embedCodeInput = shareOverlay.contentEl().querySelector('#embed-code-input');
        if (embedCodeInput) embedCodeInput.value = embedCode;

        // update direct link with timecode
        const directLinkInput = shareOverlay.contentEl().querySelector('#direct-link-input');
        if (directLinkInput) directLinkInput.value = directLink;

        //update social links with timecode
        const facebookShareAnchor = shareOverlay.el().querySelector('.facebook-icon > a');
        facebookShareAnchor.setAttribute('href', `${FACEBOOK_SHARE_URL}${directLink}`);
        const twitterShareAnchor = shareOverlay.el().querySelector('.twitter-icon > a');
        twitterShareAnchor.setAttribute('href', `${TWITTER_SHARE_URL}${directLink}`);
        const tumblrShareAnchor = shareOverlay.el().querySelector('.tumblr-icon > a');
        tumblrShareAnchor.setAttribute('href', `${TUMBLR_SHARE_URL}${directLink}`);
    })
    .ignoreElements();


const updateTimecodeShareEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_TIME_UPDATE)
    .throttleTime(100)
    .filter(() => !!options.share)
    .map(() => player.getChild(Controls.SHARE_OVERLAY)) // get share overlay
    .filter(shareOverlay => !!shareOverlay) // only proceed if there is a share overlay up
    .map(shareOverlay => shareOverlay.contentEl().querySelector('#timecode-selection')) // get timecode checkbox
    .filter(timecodeCheckbox => !!timecodeCheckbox) // only proceed if there is a timecode checkbox
    .map(timecodeCheckbox => timecodeCheckbox.checked)
    .do(isChecked => {
        // update timecode display
        const shareOverlay = player.getChild(Controls.SHARE_OVERLAY);
        const timecode = (isChecked) ? timecodeFromSeconds(player.currentTime()) : undefined;
        const timecodeText = shareOverlay.contentEl().querySelector('.timecode');
        const timecodeInput = shareOverlay.contentEl().querySelector('#timecode-input');
        if (timecode && timecodeText) timecodeText.innerHTML = timecode;
        if (timecode && timecodeInput) timecodeInput.value = timecode;

        const directLink = getDirectLink(store.getState().videoSource, options, timecode);
        const embedCode = getEmbedCode(store.getState().videoSource, options, timecode);


        // update embed code value
        const embedCodeInput = shareOverlay.contentEl().querySelector('#embed-code-input');
        if (embedCodeInput) embedCodeInput.value = embedCode;

        // update direct link with timecode
        const directLinkInput = shareOverlay.contentEl().querySelector('#direct-link-input');
        if (directLinkInput) directLinkInput.value = directLink;

        //update social links with timecode
        const facebookShareAnchor = shareOverlay.el().querySelector('.facebook-icon > a');
        if (facebookShareAnchor) facebookShareAnchor.setAttribute('href', `${FACEBOOK_SHARE_URL}${directLink}`);
        const twitterShareAnchor = shareOverlay.el().querySelector('.twitter-icon > a');
        if (twitterShareAnchor) twitterShareAnchor.setAttribute('href', `${TWITTER_SHARE_URL}${directLink}`);
        const tumblrShareAnchor = shareOverlay.el().querySelector('.tumblr-icon > a');
        if (tumblrShareAnchor) tumblrShareAnchor.setAttribute('href', `${TUMBLR_SHARE_URL}${directLink}`);
    })
    .ignoreElements();


const onCopyClickEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.ON_COPY_CLICK)
    .pluck('payload', 'target', 'dataset', 'copyTarget')
    .do(copyTarget => {
        const shareOverlay = player.getChild(Controls.SHARE_OVERLAY);
        const textInput = shareOverlay.el().querySelector(copyTarget);
        textInput.select();
        document.execCommand('copy');
    })
    .ignoreElements();


const closeShareOverlayEpic = (action$, store, { player, options }) => Observable.merge(
        action$.ofType(ActionTypes.ON_SHARE_CONTROL_CLOSE),
        action$.ofType(ActionTypes.SET_VIDEO_SOURCE)
    )
    .do(() => {
        player.removeChild(Controls.SHARE_OVERLAY);
        player.play();
    })
    .ignoreElements();



export default combineEpics(
    closeShareOverlayEpic,
    showShareOverlayEpic,
    useTimecodeShareEpic,
    updateTimecodeShareEpic,
    onCopyClickEpic,
    onTimecodeInputChangeEpic
);




// WEBPACK FOOTER //
// ./src/ui/share/epics.js